import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import toast from "react-hot-toast";
import { UserListingProps } from "../../utils/interface/userListing";
import { extractApiError } from "../../utils/util";
import { deleteLocalUser, getAllUsers } from "../../api/users/user";
import UserListing from "../../components/Users/UserListing";
import { Modal } from "../../components/UI/Modal";
import Pagination from "../../components/UI/Pagination";
import { AlertTriangle } from "lucide-react";
import UserProfileCard from "../../components/UserProfileCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SearchInput from "../../components/UI/SearchInput";
import { queryKeys } from "../../utils/queryKeys";

type PaginationType = {
  page: number;
  limit: number;
  totalPages: number;
  currentlyShowing: number;
  totalItems?: number;
  totalRecords?: number;
};

const UserManagement = () => {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<
    UserListingProps["userList"][0] | null
  >(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const queryClient = useQueryClient();

  const { data, isLoading: loading } = useQuery<{
    users?: UserListingProps["userList"];
    pagination?: PaginationType;
  }>({
    queryKey: queryKeys.users.list({
      page: currentPage,
      limit: 8,
      search: debouncedSearch,
    }),
    queryFn: async () => getAllUsers(currentPage, 8, debouncedSearch),
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => deleteLocalUser(userId),
    onSuccess: async () => {
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
    onError: (error) => {
      toast.error(extractApiError(error));
    },
  });

  const handleDeleteUser = async (id: string) => {
    await deleteUserMutation.mutateAsync(id);
  };

  const handleEditUser = (user: UserListingProps["userList"][0]) => {
    navigate({
      to: "/user-management/edit/$id",
      params: { id: user.id },
      state: { user },
    });
  };

  const handleDeleteClick = (user: UserListingProps["userList"][0]) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  // Debounce search: update debouncedSearch and reset to page 1 when user types
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const userList = data?.users ?? [];
  const pagination = data?.pagination ?? null;

  return (
    <>
      <UserProfileCard />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-[60px]">
          <div>
            <h1 className="text-[34px] lg:text-[38px] xl:text-[56px] font-display font-light text-white mb-2">
              Manage Users
            </h1>
            <p className="text-white text-lg font-light">
              Here is the list of users currently on the system
            </p>
          </div>
          <Link
            to="/user-management/add"
            className="text-base font-semibold group flex items-center min-w-[178px] gap-2 px-5 py-[6px] rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] text-white transition-all duration-300 ease-out hover:bg-transparent hover:text-[#E761A0] active:scale-95 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] add-user ml-auto"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36.874"
              height="31.79"
              viewBox="0 0 36.874 31.79"
              className="w-[36px] h-[36px] text-white transition-all duration-300 ease-out group-hover:text-[#E761A0]"
              fill="currentColor"
            >
              <g
                id="Group_86"
                data-name="Group 86"
                transform="translate(-1345.281 -385.606)"
              >
                <path
                  id="Path_228"
                  data-name="Path 228"
                  d="M324.531,185.249A6.473,6.473,0,1,0,331,191.722a6.473,6.473,0,0,0-6.473-6.473"
                  transform="translate(1036.248 205.613)"
                />
                <path
                  id="Path_229"
                  data-name="Path 229"
                  d="M30.234,41.724a8,8,0,0,1-1.186-.088,13.686,13.686,0,0,1-3.294,10.836A12.026,12.026,0,0,0,15.5,46.942,12.025,12.025,0,0,0,5.243,52.472,13.665,13.665,0,0,1,22.562,31.74,7.881,7.881,0,0,1,22.92,30.7q.149-.351.329-.685A15.5,15.5,0,1,0,31,43.437a15.744,15.744,0,0,0-.1-1.741q-.331.027-.667.028"
                  transform="translate(1345.281 358.459)"
                />
                <path
                  id="Path_230"
                  data-name="Path 230"
                  d="M838.07,0a6.641,6.641,0,1,0,0,13.281q.236,0,.469-.016A6.641,6.641,0,0,0,838.07,0"
                  transform="translate(537.445 385.602)"
                />
                <path
                  id="Path_231"
                  fill="#e761a0"
                  data-name="Path 231"
                  d="M916.536,80.663h-2.121V78.542a1.162,1.162,0,1,0-2.324,0v2.121H909.97a1.162,1.162,0,0,0-1.162,1.162h0a1.162,1.162,0,0,0,1.162,1.162h2.121v2.121a1.162,1.162,0,1,0,2.324,0V82.987h2.121a1.162,1.162,0,0,0,1.162-1.162h0a1.162,1.162,0,0,0-1.162-1.162"
                  transform="translate(462.262 310.421)"
                />
              </g>
            </svg>
            <span className="uppercase tracking-wide">Add User</span>
          </Link>
        </div>

        <div className="">
          <div className="">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="SEARCH"
              className="max-w-md"
            />
          </div>
          <div className="">
            <UserListing
              userList={userList}
              onDeleteUser={handleDeleteUser}
              onEditUser={handleEditUser}
              onDeleteClick={handleDeleteClick}
              loading={loading}
            />

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (() => {
              const totalCount = pagination.totalItems ?? pagination.totalRecords ?? pagination.currentlyShowing;
              const showingFrom = Math.min((currentPage - 1) * pagination.limit + 1, totalCount);
              const showingTo = Math.min(currentPage * pagination.limit, totalCount);
              return (
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={totalCount}
                  itemsPerPage={pagination.limit}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                />
              );
            })()}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            if (!deleteUserMutation.isPending) {
              setIsDeleteModalOpen(false);
              setSelectedUser(null);
            }
          }}
          className="max-w-md bg-slate-800/95 backdrop-blur-xl border border-white/10 shadow-2xl"
        >
          <div className="p-8">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600/20 border border-red-600/30 mb-4 mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white text-center mb-2">
              Delete User
            </h2>
            <p className="text-gray-400 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-white">
                {selectedUser?.fullName}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedUser(null);
                }}
                disabled={deleteUserMutation.isPending}
                className="flex-1 px-4 py-3 border border-white/20 text-gray-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedUser) {
                    handleDeleteUser(selectedUser.id);
                  }
                }}
                disabled={deleteUserMutation.isPending}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 transition-all duration-200 shadow-lg shadow-red-600/20 hover:shadow-red-600/30 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleteUserMutation.isPending && (
                  <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                )}
                <span>{deleteUserMutation.isPending ? "Deleting..." : "Delete User"}</span>
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default UserManagement;
export { default as AddUser } from "./AddUser";
export { default as EditUser } from "./EditUser";
